import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '../../contexts/ThemeContext';

const Header = ({ userInfo, onLogout, isMobile = false }) => {
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const dropdownRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const { isDarkMode, toggleDarkMode } = useTheme();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsProfileDropdownOpen(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    setIsProfileDropdownOpen(false);
    setIsMobileMenuOpen(false);
    onLogout();
  };


  // Get user initials for avatar
  const getUserInitials = () => {
    if (userInfo?.prenom && userInfo?.nom) {
      return `${userInfo.prenom.charAt(0)}${userInfo.nom.charAt(0)}`.toUpperCase();
    }
    return userInfo?.userName?.charAt(0)?.toUpperCase() || 'U';
  };

  // Get logo for avatar (admin: show logo, user: show client logo if available)
  const getAvatarLogo = () => {
    if (userInfo?.isAdmin) {
      // Use default logo for admin
      return '/assets/images/logo2.jpg';
    }
    if (userInfo?.logo) {
      // If logo is base64, show it
      if (userInfo.logo.startsWith('data:image')) return userInfo.logo;
      // Else assume it's base64 string
      return `data:image/jpeg;base64,${userInfo.logo}`;
    }
    return null;
  };

  // Format user display name
  const getDisplayName = () => {
    if (userInfo?.fullName && userInfo.fullName !== ' ') {
      return userInfo.fullName;
    }
    if (userInfo?.prenom || userInfo?.nom) {
      return `${userInfo?.prenom || ''} ${userInfo?.nom || ''}`.trim();
    }
    return userInfo?.userName || 'Utilisateur';
  };

  // Get user role display
  const getUserRole = () => {
    return userInfo?.isAdmin ? 'Administrateur' : 'Utilisateur';
  };

  // Mobile version - just the avatar button
  if (isMobile) {
    return (
      <div className="relative" ref={mobileMenuRef}>
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="flex items-center space-x-2 p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors focus:outline-none"
        >
          {getAvatarLogo() ? (
            <img
              src={getAvatarLogo()}
              alt="avatar"
              className="w-8 h-8 rounded-full object-contain shadow-sm border border-gray-200 dark:border-gray-700 bg-white"
              style={{ backgroundColor: 'white', transform: 'scale(1.18)' }}
            />
          ) : (
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-semibold shadow-sm ${
              isDarkMode 
                ? 'bg-gradient-to-br from-dark-primary-500 to-dark-primary-600' 
                : 'bg-gradient-to-br from-primary-500 to-primary-600'
            }`}>
              {getUserInitials()}
            </div>
          )}
        </button>

        {/* Mobile Dropdown */}
        {isMobileMenuOpen && (
          <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 py-2 z-50">
            {/* User Info */}
            <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
              <div className="flex items-center space-x-3">
              {getAvatarLogo() ? (
              <img
                src={getAvatarLogo()}
                alt="avatar"
                className="w-10 h-10 rounded-full object-contain shadow-sm border border-gray-200 dark:border-gray-700 bg-white"
                style={{ backgroundColor: 'white', transform: 'scale(1.18)' }}
              />
              ) : (
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold ${
                  isDarkMode 
                    ? 'bg-gradient-to-br from-dark-primary-500 to-dark-primary-600' 
                    : 'bg-gradient-to-br from-primary-500 to-primary-600'
                }`}>
                  {getUserInitials()}
                </div>
              )}
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-gray-900 dark:text-white truncate">
                    {getDisplayName()}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    ID: {userInfo?.idUser}
                  </div>
                </div>
              </div>
            </div>

            {/* Menu Items */}
            <div className="py-2">
              <button 
                onClick={toggleDarkMode}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center transition-colors"
              >
                <i className={`${isDarkMode ? 'fas fa-sun' : 'fas fa-moon'} mr-3 text-gray-400 dark:text-gray-500 w-4`}></i>
                {isDarkMode ? 'Mode Clair' : 'Mode Sombre'}
              </button>
              
              <div className="border-t border-gray-100 dark:border-gray-700 my-2"></div>

              <button
                onClick={handleLogout}
                className="w-full px-4 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center transition-colors group"
              >
                <i className="fas fa-sign-out-alt mr-3 text-red-500 dark:text-red-400 w-4 group-hover:text-red-600 dark:group-hover:text-red-300"></i>
                <span className="flex-1">Se déconnecter</span>
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Desktop version - full header
  return (
    <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700 h-16 flex-shrink-0 transition-colors">
      <div className="flex items-center justify-between h-full px-4 sm:px-6">
        {/* Left side - Title */}
        <div className="flex items-center space-x-4 min-w-0">
          <div className={`text-xl sm:text-2xl font-bold truncate transition-colors ${
            isDarkMode ? 'text-dark-primary-400' : 'text-primary-600'
          }`}>
            Administration
          </div>
        </div>
        
        {/* Right side - Actions */}
        <div className="flex items-center space-x-2 sm:space-x-4 flex-shrink-0">
          {/* Dark Mode Toggle */}
          <button
            onClick={toggleDarkMode}
            className={`p-2 rounded-lg transition-all hover:scale-105 ${
              isDarkMode 
                ? 'bg-dark-primary-100 text-dark-primary-600 hover:bg-dark-primary-200' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            title={isDarkMode ? 'Passer en mode clair' : 'Passer en mode sombre'}
          >
            <i className={`${isDarkMode ? 'fas fa-sun' : 'fas fa-moon'} text-lg`}></i>
          </button>


          {/* Profile Dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
              className={`flex items-center space-x-2 sm:space-x-3 p-1 sm:p-2 rounded-lg transition-colors focus:outline-none focus:ring-2 ${
                isDarkMode 
                  ? 'hover:bg-gray-700 focus:ring-dark-primary-500' 
                  : 'hover:bg-gray-50 focus:ring-primary-500'
              }`}
            >
              {/* Avatar */}
              {getAvatarLogo() ? (
                <img
                  src={getAvatarLogo()}
                  alt="avatar"
                  className="w-12 h-12 rounded-full object-contain shadow-sm border border-gray-200 dark:border-gray-700 bg-white"
                  style={{ backgroundColor: 'white', transform: 'scale(1.18)' }}
                />
              ) : (
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-semibold shadow-sm ${
                  isDarkMode 
                    ? 'bg-gradient-to-br from-dark-primary-500 to-dark-primary-600' 
                    : 'bg-gradient-to-br from-primary-500 to-primary-600'
                }`}>
                  {getUserInitials()}
                </div>
              )}
              
              {/* User Info - Hidden on mobile */}
              <div className="hidden lg:block text-left">
                <div className="text-sm font-medium text-gray-900 dark:text-white max-w-32 truncate">
                  {getDisplayName()}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center">
                  {userInfo?.isAdmin ? (
                    <>
                      <i className="fas fa-crown text-yellow-500 mr-1"></i>
                      Admin
                    </>
                  ) : (
                    <>Client</>
                  )}
                </div>
              </div>

              {/* Dropdown Arrow */}
              <i className={`fas fa-chevron-down text-gray-400 dark:text-gray-500 text-xs transition-transform ${
                isProfileDropdownOpen ? 'rotate-180' : ''
              }`}></i>
            </button>

            {/* Dropdown Menu */}
            {isProfileDropdownOpen && (
              <div className="absolute right-0 mt-2 w-72 sm:w-80 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 py-2 z-50">
                {/* User Profile Header */}
                <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
                  <div className="flex items-center space-x-3">
                    {getAvatarLogo() ? (
                    <img
                      src={getAvatarLogo()}
                      alt="avatar"
                      className="w-12 h-12 rounded-full object-contain shadow-sm border border-gray-200 dark:border-gray-700 bg-white"
                      style={{ backgroundColor: 'white', transform: 'scale(1.18)' }}
                    />
                    ) : (
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-semibold ${
                        isDarkMode 
                          ? 'bg-gradient-to-br from-dark-primary-500 to-dark-primary-600' 
                          : 'bg-gradient-to-br from-primary-500 to-primary-600'
                      }`}>
                        {getUserInitials()}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-gray-900 dark:text-white truncate">
                        {getDisplayName()}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        ID: {userInfo?.idUser}
                      </div>
                      <div className="text-xs text-gray-400 dark:text-gray-500 flex items-center mt-1">
                        {userInfo?.isAdmin && (
                          <>
                            <i className="fas fa-crown text-yellow-500 mr-1"></i>
                            <span>Administrateur système</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Menu Options */}
                <div className="py-2">

                  <button 
                    onClick={toggleDarkMode}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center transition-colors"
                  >
                    <i className={`${isDarkMode ? 'fas fa-sun' : 'fas fa-moon'} mr-3 text-gray-400 dark:text-gray-500 w-4`}></i>
                    {isDarkMode ? 'Mode Clair' : 'Mode Sombre'}
                  </button>

                  <div className="border-t border-gray-100 dark:border-gray-700 my-2"></div>

                  {/* Logout */}
                  <button
                    onClick={handleLogout}
                    className="w-full px-4 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center transition-colors group"
                  >
                    <i className="fas fa-sign-out-alt mr-3 text-red-500 dark:text-red-400 w-4 group-hover:text-red-600 dark:group-hover:text-red-300"></i>
                    <span className="flex-1">Se déconnecter</span>
                    <i className="fas fa-arrow-right ml-2 text-xs opacity-0 group-hover:opacity-100 transition-opacity"></i>
                  </button>
                </div>

                {/* Footer */}
                <div className="px-4 py-2 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
                  <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center justify-between">
                    <span>Connecté depuis</span>
                    <span className="font-mono">
                      {new Date().toLocaleDateString('fr-FR')}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;